import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useForm, ValidationError } from '@formspree/react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ContactSection = () => {
  const { t } = useTranslation();
  const [state, handleSubmit] = useForm("YOUR_FORMSPREE_ID"); // TODO: Replace with your Formspree ID
  const morenaPosition = [54.373, 18.58];

  const contactInfo = [
    { icon: <FaEnvelope />, label: 'Email', value: 'zawadzkib333@gmail.com', href: 'mailto:zawadzkib333@gmail.com' },
    { icon: <FaPhone />, label: 'Telefon', value: '+48 733822711', href: 'tel:+48733822711' },
    { icon: <FaMapMarkerAlt />, label: 'Lokalizacja', value: 'Gdańsk, Polska' },
  ];
  
  if (state.succeeded) {
      return <p>Thanks for joining!</p>;
  }

  return (
    <ContactContainer id="contact">
      <div className="container">
        <div className="section-title">
          <h2>{t('contact.title')}</h2>
        </div>
        <ContactGrid>
          <InfoContainer>
            {contactInfo.map((item, index) => (
              <InfoItem key={index}>
                <InfoIcon>{item.icon}</InfoIcon>
                <InfoText>
                  <strong>{item.label}:</strong>
                  {item.href ? <a href={item.href}>{item.value}</a> : <span>{item.value}</span>}
                </InfoText>
              </InfoItem>
            ))}
            <ContactForm onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="email">{t('contact.email')}</FormLabel>
                <FormInput id="email" type="email" name="email" />
                <ValidationError prefix="Email" field="email" errors={state.errors} />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="message">{t('contact.message')}</FormLabel>
                <FormTextarea id="message" name="message" rows="5" />
                <ValidationError prefix="Message" field="message" errors={state.errors} />
              </FormGroup>
              <FormButton type="submit" disabled={state.submitting}>
                <FaPaperPlane /> {t('contact.send')}
              </FormButton>
            </ContactForm>
          </InfoContainer>
          <MapWrapper>
            <MapContainer center={morenaPosition} zoom={14} scrollWheelZoom={false}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              />
              <Marker position={morenaPosition}><Popup>Gdańsk Morena</Popup></Marker>
            </MapContainer>
          </MapWrapper>
        </ContactGrid>
      </div>
    </ContactContainer>
  );
};


const ContactContainer = styled.section`
  padding: 5rem 0;
  background-color: transparent;
  position: relative;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;
  background-color: var(--gray-100);
  padding: 3rem;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
  color: var(--primary-color);
  flex-shrink: 0;
  width: 30px;
  text-align: center;
`;

const InfoText = styled.div`
  font-size: 1.1rem;
  color: var(--text-color);
  
  strong {
    display: block;
    color: var(--text-light);
    margin-bottom: 0.25rem;
    font-size: 0.9rem;
    font-weight: 400;
  }
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MapWrapper = styled.div`
  height: 100%;
  min-height: 400px;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  
  .leaflet-container {
    width: 100%;
    height: 100%;
  }
`;

const ContactForm = styled.form`
  margin-top: 2rem;
  border-top: 1px solid var(--gray-200);
  padding-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-light);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-family: var(--font-primary);
  font-size: 1rem;
  background-color: var(--gray-200);
  color: var(--text-color);
  transition: all var(--transition-fast) ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(57, 255, 20, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-family: var(--font-primary);
  font-size: 1rem;
  resize: vertical;
  background-color: var(--gray-200);
  color: var(--text-color);
  transition: all var(--transition-fast) ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(57, 255, 20, 0.2);
  }
`;

const FormButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--black);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  
  &:hover:not(:disabled) {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export default ContactSection;